import { useState } from 'react';

import type {
  CashAccountChoice,
  CreateExpenseInput,
  DueExpense,
  Expense,
  ExpenseHead,
  ExpenseSummary,
} from '@/api/cash';
import { formatDate } from '@core/format/dates';
import type { PaymentMethod } from '@/api/generated/enums';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { QuickAmounts, SplitBoxes } from '@/components/SplitBoxes';
import { useSplitParts } from '@/components/useSplitParts';
import { HeadField } from './HeadField';
import {
  expenseParts,
  expensePartsProblem,
  expenseRequests,
  payableHeads,
  saveExpensesLabel,
} from '@core/cash/expense';
import { formatAmount } from '@core/format/money';
import {
  filledParts,
  splitTotal,
  splitWords,
  withoutSaved,
  type PaidInParts,
  type SplitPart,
} from '@core/cash/split';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import { toIsoDate } from '@core/format/dates';

/** What the form opens with, besides a due item: "Again, like last time" fills these. */
export interface ExpenseStart {
  headId: number;
  method: PaymentMethod;
  cashAccountId: number | null;
}

const rupees = (value: number) => `₹${formatAmount(value)}`;
const methodWord = (method: SplitPart['method']) =>
  method === 'Cash' ? 'cash' : paymentMethodLabel[method];

/**
 * Recording money going out (spec Section 18), drawn to board 4 of the canvas "Cash & expense flow":
 * what for, when, how much from which account, for the record — with the voucher as it will be saved
 * beside it.
 *
 * <p>
 * <strong>A page, not a drawer</strong>, per the rule settled 2026-09-08: a screen that writes a
 * record is a page. It is reached by internal state from the register beside it, because the shell
 * navigates by key and has no router.
 * </p>
 *
 * <p>
 * <strong>A box per account</strong> (owner, 2026-11-01, split payments step 6): ₹500 from the till
 * and ₹700 by UPI from the bank is <strong>two vouchers</strong>, one per account, each in its own
 * day book (core `cash/expense.ts` `expenseRequests`). A bank's box says how; cash comes out of a
 * till or petty cash. It never offers `Adjustment`. A recurring expense stays one voucher.
 * </p>
 */
export function NewExpensePage({
  heads,
  accounts,
  from,
  start,
  usage,
  branchName,
  busy = false,
  mayAddHead = true,
  onCancel,
  onSave,
}: {
  heads: ExpenseHead[];
  accounts: CashAccountChoice[] | undefined;
  /**
   * The recurring expense this voucher pays (step 8): the form opens filled from it, the amount
   * still changeable, and the voucher settles that occurrence.
   */
  from?: DueExpense | undefined;
  /** "Again, like last time": head, method and account, never the amount. */
  start?: ExpenseStart | undefined;
  /** This month's spending, which puts the most used heads first on the tiles. */
  usage?: ExpenseSummary | undefined;
  branchName?: string | undefined;
  busy?: boolean;
  /** Whether the head picker may offer to add one (`Expense:Create`). */
  mayAddHead?: boolean;
  onCancel: () => void;
  /**
   * `again`: Save and record another. Answers what was saved; a refusal part-way leaves the page
   * open, and the page says so.
   */
  onSave: (
    requests: CreateExpenseInput[],
    parts: readonly SplitPart[],
    again: boolean,
  ) => Promise<PaidInParts<Expense>>;
}) {
  const firstField = useAutoFocusFirstField<HTMLSelectElement>();
  const today = toIsoDate(new Date()) ?? '';

  // Empty until chosen — a clerk who pressed Enter past a pre-filled first head filed rent under
  // "Bank charges" (2026-10-23).
  const [headId, setHeadId] = useState<number | null>(from?.expenseHeadId ?? start?.headId ?? null);
  const [paidOn, setPaidOn] = useState(today);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState(from?.notes ?? '');
  const [partly, setPartly] = useState<string | null>(null);
  // A due expense's figure starts in the account it names; "Again" never brings an amount.
  const { parts, change } = useSplitParts(accounts, (fresh) =>
    expenseParts(
      fresh,
      {
        amount: from ? String(from.typicalAmount) : '',
        method: from?.method ?? start?.method ?? 'Cash',
        cashAccountId: from?.cashAccountId ?? start?.cashAccountId ?? null,
      },
      accounts,
    ),
  );

  const draft = {
    headId,
    amount: '',
    paidOn,
    method: 'Cash' as PaymentMethod,
    reference,
    cashAccountId: null,
    recurrence: from
      ? { recurringExpenseId: from.recurringExpenseId, dueOn: from.dueOn.slice(0, 10) }
      : null,
  };
  const total = splitTotal(parts);
  const used = filledParts(parts).length;
  const problem = expensePartsProblem(draft, heads, today, parts);
  const ready = problem === null;
  const head = heads.find((option) => option.id === headId);

  // The most used heads this month first, then the rest by name — the tiles; the list below still
  // holds every head, and adds one.
  const usedHeads = new Map((usage?.byHead ?? []).map((row) => [row.expenseHeadId, row.count]));
  const tiles = [...payableHeads(heads)]
    .sort(
      (a, b) =>
        (usedHeads.get(b.id) ?? 0) - (usedHeads.get(a.id) ?? 0) || a.name.localeCompare(b.name),
    )
    .slice(0, 8);

  const save = (again: boolean) => {
    const requests = expenseRequests(draft, heads, today, parts);

    if (!requests || busy) {
      return;
    }

    setPartly(null);
    void onSave(
      requests.map((request) => ({ ...request, notes: notes.trim() || null })),
      parts,
      again,
    )
      .then(({ saved, failed }) => {
        if (!failed) {
          return;
        }

        // What went through is emptied, so Save again sends only what is left.
        change((current) => withoutSaved(current, saved));
        const words = splitWords(
          saved.map((each) => each.part),
          methodWord,
          rupees,
          'from',
        );
        setPartly(`${words} — saved. ${failed.part.accountName} was not.`);
      })
      // The refusal is already on the toast; nothing was saved.
      .catch(() => undefined);
  };

  return (
    <div {...enterKeyScope}>
      <PageHeading
        eyebrow="Money out"
        title="Record an expense"
        subtitle="Money out of a drawer, filed under the head it belongs to."
        overlap
        actions={
          <>
            <Button onClick={onCancel} disabled={busy} shortcut="back">
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              shortcut="save"
              disabled={!ready || busy}
              onClick={() => save(false)}
            >
              {saveExpensesLabel(total, used, '₹')}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-12 items-start gap-4 px-4">
        <Card flush className="col-span-12 overflow-hidden xl:col-span-8">
          <fieldset className="border-border-color border-b px-5 py-4">
            <legend className="sr-only">What for</legend>
            <p className="mb-2.5 text-[15px] font-bold text-gray-900">1 · What for</p>
            {tiles.length > 0 && (
              <div
                role="group"
                aria-label="Most used heads"
                className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4"
              >
                {tiles.map((tile) => {
                  const count = usedHeads.get(tile.id) ?? 0;

                  return (
                    <button
                      key={tile.id}
                      type="button"
                      disabled={busy}
                      aria-pressed={tile.id === headId}
                      onClick={() => setHeadId(tile.id)}
                      className={`cursor-pointer rounded-xl px-3 py-2.5 text-start disabled:cursor-not-allowed ${
                        tile.id === headId
                          ? 'border-primary bg-primary-transparent border-2'
                          : 'border-border-color hover:bg-light border bg-white'
                      }`}
                    >
                      <span className="block truncate text-[14px] font-semibold text-gray-900">
                        {tile.name}
                      </span>
                      <span className="text-default block text-[12px]">
                        {count > 0 ? `${count} this month` : 'Not used this month'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <HeadField
              heads={heads}
              value={headId}
              onChange={setHeadId}
              disabled={busy}
              mayAdd={mayAddHead}
              selectRef={firstField}
            />
          </fieldset>

          <fieldset className="border-border-color border-b px-5 py-4">
            <legend className="sr-only">How much, from which account</legend>
            <p className="mb-2.5 text-[15px] font-bold text-gray-900">
              2 · How much, from which account
            </p>
            <SplitBoxes parts={parts} onChange={change} direction="from" busy={busy} />
            {from && (
              <div className="mt-2.5">
                <QuickAmounts
                  options={[
                    { label: `Usual ${rupees(from.typicalAmount)}`, amount: from.typicalAmount },
                  ]}
                  busy={busy}
                  onPick={(amount) =>
                    change((current) =>
                      expenseParts(
                        current.map((part) => ({ ...part, amount: '' })),
                        {
                          amount: String(amount),
                          method: from.method,
                          cashAccountId: from.cashAccountId,
                        },
                        accounts,
                      ),
                    )
                  }
                />
              </div>
            )}
            <p className="text-default mt-2.5 mb-0 text-[12px]">
              Cash comes out of a till or petty cash; UPI, card and bank transfer out of a bank
              account. Two accounts make two vouchers.
            </p>
          </fieldset>

          <fieldset className="grid gap-4 px-5 py-4 sm:grid-cols-3">
            <legend className="sr-only">When, and for the record</legend>
            <p className="mb-0 text-[15px] font-bold text-gray-900 sm:col-span-3">
              3 · When, and for the record
            </p>
            <Field label="Paid on" required hint="Never in the future.">
              <input
                type="date"
                disabled={busy}
                value={paidOn}
                onChange={(event) => setPaidOn(event.target.value)}
                className={controlClass}
              />
            </Field>
            <Field label="Bill number" hint="On every voucher.">
              <input
                type="text"
                disabled={busy}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                className={controlClass}
              />
            </Field>
            <Field label="Notes" hint="Who it went to, and what for.">
              <input
                type="text"
                disabled={busy}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className={controlClass}
              />
            </Field>
            {partly && (
              <div className="sm:col-span-3">
                <Notice tone="warning" title="Part of it was saved">
                  {partly}
                </Notice>
              </div>
            )}
            {problem && used > 0 && (
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)] sm:col-span-3">
                {problem}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2 sm:col-span-3">
              <Button disabled={!ready || busy} onClick={() => save(true)}>
                Save and record another
              </Button>
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                disabled={!ready || busy}
                onClick={() => save(false)}
              >
                {saveExpensesLabel(total, used, '₹')}
              </Button>
            </div>
          </fieldset>
        </Card>

        <aside aria-label="The voucher" className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card flush className="overflow-hidden">
            <div className="bg-primary-transparent border-primary/40 flex items-baseline justify-between border-b border-dashed px-5 py-3.5">
              <span className="text-primary text-[12px] font-bold tracking-[0.12em] uppercase">
                {used > 1 ? `${used} expense vouchers` : 'Expense voucher'}
              </span>
              <span className="text-primary text-[12px]">EXP · numbered on save</span>
            </div>
            <div className="flex flex-col gap-2.5 px-5 py-4">
              <p className="mb-0 font-serif text-[30px] lining-nums tabular-nums font-semibold text-gray-900">
                {total > 0 ? rupees(total) : '₹—'}
              </p>
              <SlipLine label="Head" value={head?.name ?? '—'} />
              {filledParts(parts).map((part) => (
                <SlipLine
                  key={part.accountId}
                  label={part.accountName}
                  value={`${rupees(Number(part.amount.replace(/,/g, '')) || 0)} · ${paymentMethodLabel[part.method]}`}
                />
              ))}
              {used === 0 && <SlipLine label="From" value="—" />}
              <SlipLine label="Day" value={paidOn ? formatDate(paidOn) : '—'} />
              {branchName && <SlipLine label="Branch" value={branchName} />}
            </div>
          </Card>

          {from && (
            <Notice tone="warning" title={`${from.name} — due ${formatDate(from.dueOn)}`}>
              Filled in from the recurring expense; change the amount if this one differs. Saving
              records this occurrence as paid — from one account.
            </Notice>
          )}

          <Notice tone="info" title="A voucher is never edited">
            A mistake is voided with a reason, and the money goes back into the same account on the
            day it is voided.
          </Notice>

          {!heads.some((option) => option.isActive) && !mayAddHead && (
            <Notice tone="warning" title="No expense heads yet">
              Spending is filed under a head — rent, electricity, staff welfare. Add one on the
              register before recording anything.
            </Notice>
          )}
        </aside>
      </div>
    </div>
  );
}

function SlipLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-[13px]">
      <span className="text-default">{label}</span>
      <span className="truncate font-semibold text-gray-900">{value}</span>
    </div>
  );
}
